import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, CalendarIcon, Users, BookOpen, Award, TrendingUp, MessageSquare, BarChart, Star } from "lucide-react";
import FeedbackDetailCard from "@/components/feedback-reports/FeedbackDetailCard";
import { useToast } from "@/components/ui/use-toast";
import {
  PieChart,
  Pie,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";

interface FeedbackItem {
  id: string;
  course_id: string;
  user: {
    name: string;
    position: string;
    school: string;
  };
  course: {
    title: string;
    category: string;
    id: string;
  };
  rating: number;
  feedback: string;
  date: string;
  recommendRating?: number;
  position?: string;
}

interface CourseDetails {
  id: string;
  title: string;
  description: string;
  category: string;
  presenter: string;
  created_at: string;
  start_date?: string;
  total_participants?: number;
}

const CourseFeedbackDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [feedbackData, setFeedbackData] = useState<FeedbackItem[]>([]);
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails();
      fetchFeedbackData();
    }
  }, [courseId]);

  const fetchCourseDetails = async () => {
    try {
      const { data, error } = await supabase
        .from("courses")
        .select(`
          id,
          title,
          description,
          category,
          presenter,
          created_at,
          start_date
        `)
        .eq("id", courseId)
        .single();

      if (error) {
        console.error("Error fetching course details:", error);
        toast({
          title: "Error",
          description: "Failed to load course details",
          variant: "destructive",
        });
        return;
      }

      const { count } = await supabase
        .from("course_registrations")
        .select("id", { count: "exact", head: true })
        .eq("course_id", courseId);

      setCourseDetails({
        ...data,
        total_participants: count || 0
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const fetchFeedbackData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("course_registrations")
        .select(`
          id,
          course_id,
          overall_rating,
          feedback,
          recommend_rating,
          registration_date,
          courses (
            id,
            title,
            category
          ),
          profiles (
            first_name,
            last_name,
            position,
            school
          )
        `)
        .eq("course_id", courseId)
        .not("feedback", "is", null);

      if (error) {
        console.error("Error fetching feedback:", error);
        toast({
          title: "Error",
          description: "Failed to load feedback data",
          variant: "destructive",
        });
        return;
      }

      const formattedData: FeedbackItem[] = data.map((item: any) => ({
        id: item.id,
        course_id: item.course_id,
        user: {
          name: `${item.profiles?.first_name || ""} ${item.profiles?.last_name || ""}`,
          position: item.profiles?.position || "Unknown",
          school: item.profiles?.school || "Unknown",
        },
        course: {
          id: item.courses?.id,
          title: item.courses?.title || "Unknown",
          category: item.courses?.category || "Uncategorized",
        },
        rating: item.overall_rating || 0,
        feedback: item.feedback || "No feedback provided",
        date: item.registration_date || "Unknown",
        recommendRating: item.recommend_rating,
        position: item.profiles?.position,
      }));
      
      setFeedbackData(formattedData);
    } catch (error) {
      console.error("Failed to fetch feedback data:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalFeedback = feedbackData.length;
  const avgRating = feedbackData.reduce((sum, item) => sum + item.rating, 0) / totalFeedback || 0;
  const recommendPercentage = feedbackData.filter(item => item.recommendRating && item.recommendRating >= 3).length / totalFeedback * 100 || 0;

  const ratingDistribution = [...Array(4)].map((_, i) => ({
    rating: i + 1,
    count: feedbackData.filter(item => item.rating === i + 1).length,
    percentage: (feedbackData.filter(item => item.rating === i + 1).length / totalFeedback * 100).toFixed(1)
  }));

  const getRoleDistribution = () => {
    const roleCounts: Record<string, number> = {};
    
    feedbackData.forEach(item => {
      const role = item.position || "Not specified";
      if (!roleCounts[role]) {
        roleCounts[role] = 0;
      }
      roleCounts[role]++;
    });
    
    return Object.entries(roleCounts).map(([name, value]) => ({
      name,
      value
    }));
  };

  const COLORS = ['#004161', '#0f8f8c', '#cfb9a2', '#b1c3cf', '#f1eceb'];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex flex-col space-y-2">
        <Button
          variant="ghost"
          className="self-start flex items-center text-primary mb-2"
          onClick={() => navigate("/admin/feedback")}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to All Feedback
        </Button>
        
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight font-title text-primary">
              {courseDetails?.title || "Course Feedback"}
            </h1>
            <p className="text-muted-foreground font-subtitle mt-1">
              Detailed feedback analysis and insights
            </p>
          </div>
          
          <Badge 
            variant="outline" 
            className="text-md py-1.5 px-3 border-primary/30 text-primary self-start"
          >
            {courseDetails?.category || "Category"}
          </Badge>
        </div>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-6 flex justify-center items-center min-h-[200px]">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
              <p>Loading course feedback...</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                <div className="col-span-1 md:col-span-2 lg:col-span-3">
                  <h2 className="text-xl font-bold mb-2 font-heading text-primary">About the Course</h2>
                  <p className="text-primary/90 font-body text-sm mb-4">
                    {courseDetails?.description || "No description available."}
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="p-1">
                        <Users className="h-3.5 w-3.5 text-accent" />
                      </Badge>
                      <div className="text-sm">
                        <p className="text-muted-foreground">Participants</p>
                        <p className="font-medium">{courseDetails?.total_participants || 0}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="p-1">
                        <CalendarIcon className="h-3.5 w-3.5 text-accent" />
                      </Badge>
                      <div className="text-sm">
                        <p className="text-muted-foreground">Date</p>
                        <p className="font-medium">
                          {courseDetails?.start_date 
                            ? new Date(courseDetails.start_date).toLocaleDateString() 
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="p-1">
                        <BookOpen className="h-3.5 w-3.5 text-accent" />
                      </Badge>
                      <div className="text-sm">
                        <p className="text-muted-foreground">Presenter</p>
                        <p className="font-medium">{courseDetails?.presenter || "Not specified"}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="col-span-1 border-l pl-6">
                  <h3 className="text-lg font-bold mb-3 font-heading flex items-center gap-2 text-primary">
                    <Award className="h-5 w-5 text-accent" />
                    Feedback Score
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Average Rating</p>
                      <div className="flex items-center">
                        <span className="text-3xl font-bold mr-2 text-primary">{avgRating.toFixed(1)}</span>
                        <div className="flex">
                          {[...Array(4)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-5 w-5 ${i < Math.round(avgRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">From {totalFeedback} responses</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Would Recommend</p>
                      <div className="flex items-center">
                        <span className="text-3xl font-bold mr-2 text-primary">{recommendPercentage.toFixed(0)}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Of participants</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-accent-subtle/50">
              <TabsTrigger value="overview" className="data-[state=active]:bg-accent data-[state=active]:text-white">
                <TrendingUp className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="feedback" className="data-[state=active]:bg-accent data-[state=active]:text-white">
                <MessageSquare className="h-4 w-4 mr-2" />
                Feedback
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-accent data-[state=active]:text-white">
                <BarChart className="h-4 w-4 mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Rating Distribution</CardTitle>
                    <CardDescription>Breakdown of ratings received</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsBarChart
                          data={ratingDistribution}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="rating" />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name, props) => [`${value} responses`, `${props.payload.rating} stars`]}
                          />
                          <Bar dataKey="count" name="Responses">
                            {ratingDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </RechartsBarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Key Insights</CardTitle>
                    <CardDescription>Summary of feedback responses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2">Rating Summary</h4>
                        {ratingDistribution.map(item => (
                          <div key={item.rating} className="flex items-center mb-2">
                            <div className="flex items-center w-16">
                              <Star className="h-4 w-4 text-amber-400 fill-amber-400 mr-1" />
                              <span>{item.rating}</span>
                            </div>
                            <div className="w-full bg-accent-subtle/30 rounded-full h-2.5 mr-2">
                              <div 
                                className="bg-accent h-2.5 rounded-full" 
                                style={{ width: `${item.percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs w-12 text-muted-foreground">{item.percentage}%</span>
                          </div>
                        ))}
                      </div>
                      
                      <Separator />
                      
                      <div>
                        <h4 className="font-medium mb-2">Top Feedback Themes</h4>
                        <ul className="space-y-2">
                          {feedbackData.length > 0 ? (
                            feedbackData
                              .slice(0, 3)
                              .map(item => (
                                <li key={item.id} className="text-sm">
                                  "{item.feedback.length > 100 
                                    ? item.feedback.substring(0, 100) + '...' 
                                    : item.feedback}"
                                </li>
                              ))
                          ) : (
                            <li className="text-sm text-muted-foreground">No feedback themes available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Participant Roles</CardTitle>
                    <CardDescription>Roles of the feedback providers</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getRoleDistribution()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {getRoleDistribution().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name, props) => [`${value} participants`, props.payload.name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Feedback Overview</CardTitle>
                    <CardDescription>Summary of course feedback</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mt-2">
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="font-medium">Total Responses</span>
                        <span className="text-primary">{totalFeedback}</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="font-medium">Average Rating</span>
                        <div className="flex items-center">
                          <span className="mr-2">{avgRating.toFixed(1)}</span>
                          <div className="flex">
                            {[...Array(4)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${i < Math.round(avgRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between items-center pb-2 border-b">
                        <span className="font-medium">Would Recommend</span>
                        <span className="text-primary">{recommendPercentage.toFixed(0)}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
            
            <TabsContent value="feedback" className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {feedbackData.length > 0 ? (
                  feedbackData.map(feedback => (
                    <FeedbackDetailCard key={feedback.id} feedback={feedback} clickable={false} />
                  ))
                ) : (
                  <Card>
                    <CardContent className="p-6 flex justify-center items-center">
                      <p className="text-muted-foreground">No feedback available for this course</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="analytics" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Advanced Analytics</CardTitle>
                  <CardDescription>Deeper insights from course feedback</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <h3 className="text-lg font-medium mb-4">Feedback Summary</h3>
                      <div className="space-y-3">
                        <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 border-b">
                          <div className="font-medium">Average Rating</div>
                          <div className="flex items-center">
                            <span className="text-primary mr-2">{avgRating.toFixed(1)}</span>
                            <div className="flex">
                              {[...Array(4)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`h-4 w-4 ${i < Math.round(avgRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 border-b">
                          <div className="font-medium">Recommendation Rate</div>
                          <div>{recommendPercentage.toFixed(0)}% would recommend</div>
                        </div>
                        
                        <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 border-b">
                          <div className="font-medium">Total Responses</div>
                          <div>{totalFeedback} feedback submissions</div>
                        </div>
                        
                        <div className="flex flex-col md:flex-row md:items-center justify-between pb-2 border-b">
                          <div className="font-medium">Response Rate</div>
                          <div>
                            {courseDetails?.total_participants ? 
                              `${((totalFeedback / courseDetails.total_participants) * 100).toFixed(0)}% of participants` : 
                              'Unknown'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default CourseFeedbackDetails;
